/* eslint-disable import/no-named-as-default-member */
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { uniqBy } from 'lodash'
import { useEffect, useState } from 'react'

import { AccessType } from 'API'
import { listEventsForParticipantUpdate } from 'api/queries'
import { DEFAULT_ENTRIES_PER_PAGE } from 'constants/api'
import { ACCESS_TYPES, EVENT_SHIRT_DETAILS } from 'constants/models'
import { Event, FormEvent } from 'models/API'
import { fetchQuery } from 'utils/ApiUtils'
import { jsonParse } from 'utils/ObjectUtil'

import { ErrorProps } from './common'
import useNextToken from './useNextToken'

dayjs.extend(utc)
dayjs.extend(timezone)

export const useListEventsForParticipantUpdate = () => {
  const [events, setEvents] = useState<Array<Event>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorProps | null>()

  const { setNewToken, startUsingToken, onTokenSuccess } = useNextToken()

  const queryInfo = [
    {
      options: {
        filter: {
          endDateTime: {
            gt: dayjs().tz('Asia/Manila'),
          },
        },
      },
      name: 'listEvents',
      query: listEventsForParticipantUpdate,
      isFP: false,
    },
    // {
    //   options: {
    //     filter: {
    //       registrationCutOff: {
    //         gt: dayjs().tz('Asia/Manila').subtract(6, 'M'),
    //       },
    //     },
    //   },
    //   name: 'listFormEvents',
    //   query: listFormEventsForParticipantUpdate,
    //   isFP: true,
    // },
  ]

  useEffect(() => {
    let didCancel = false

    let allItems = []
    let currentNextToken = null

    let queryCount = 0

    const fetchData = async () => {
      if (didCancel) return
      if (!currentNextToken && queryCount === queryInfo.length) {
        setLoading(false)
        return
      }

      if (queryCount < queryInfo.length) {
        setLoading(true)

        if (currentNextToken) startUsingToken(currentNextToken)

        try {
          const { items, nextToken: newNextToken } = (await fetchQuery(
            queryInfo[queryCount].name,
            queryInfo[queryCount].query,
            {
              limit: DEFAULT_ENTRIES_PER_PAGE,
              ...(currentNextToken && { nextToken: currentNextToken }),
              ...queryInfo[queryCount].options,
            },
          )) as unknown as {
            items: Event[] | FormEvent[]
            nextToken: string | null
          }
          if (queryInfo[queryCount].isFP) {
            const withFP = items.reduce((prev, cur) => {
              return prev.concat({
                ...cur,
                isFP: true,
              })
            }, [])
            allItems = uniqBy([...allItems, ...withFP], 'id')
          } else allItems = uniqBy([...allItems, ...items], 'id')

          if (currentNextToken) onTokenSuccess(currentNextToken)
          const parsedItems = allItems.map((item) => ({
            ...item,
            accessType: (item.accessType ?? ACCESS_TYPES.PUBLIC) as AccessType,
            ...(item.subCategories && {
              subCategories: JSON.parse(
                JSON.parse(item.subCategories as string),
              ),
            }),
            shirtDetails: {
              ...EVENT_SHIRT_DETAILS,
              ...(item.shirtDetails && {
                ...JSON.parse(JSON.parse(item.shirtDetails as string)),
              }),
            },
            waiver: jsonParse(jsonParse(item.waiver as string)),
          }))
          setEvents(parsedItems)

          currentNextToken = newNextToken
          if (newNextToken) {
            setNewToken(newNextToken)
            fetchData()
          } else {
            queryCount++
            setNewToken(null)
            fetchData()
          }
        } catch (e) {
          console.log(e)
          setError({
            title: 'Fail to load events',
            message: '',
          })
        }
      }
    }

    fetchData()
    return () => {
      didCancel = true
    }
  }, [setNewToken])

  return {
    events,
    apiLoading: loading,
    apiError: error,
  }
}
